import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";

import { useCreateProject } from "../app/queries";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import { Textarea } from "../components/ui/textarea";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../components/ui/toggle-group";
import { Topbar } from "../components/Topbar";
import {
  Page,
  PageBody,
  PageDescription,
  PageHeader,
} from "../components/PageLayout";
import { SUGGESTED_COLORS } from "../lib/colors";

export function NewProjectPage() {
  const navigate = useNavigate();
  const [color, setColor] = useState<string>(SUGGESTED_COLORS[0]);
  const create = useCreateProject({
    success: (p) => `Created project ${p.name}`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to create project",
  });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const key = String(fd.get("key") ?? "").trim().toLowerCase();
    const name = String(fd.get("name") ?? "").trim();
    const description = String(fd.get("description") ?? "").trim();
    try {
      const p = await create.mutateAsync({ key, name, description, color });
      navigate(`/projects/${p.id}`);
    } catch {
      // error toast is handled by useCreateProject
    }
  }

  return (
    <Page>
      <Topbar
        title="New project"
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: "New project" },
        ]}
      />
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <PageHeader>
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
            <Button
              render={<Link to="/" />}
              nativeButton={false}
              type="button"
              variant="ghost"
              size="sm"
              className="self-start text-muted-foreground"
            >
              <ArrowLeftIcon data-icon="inline-start" /> Back to projects
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Create a project
              </h1>
              <PageDescription className="mt-1">
                Each project corresponds to one game and isolates its reports
                and API keys.
              </PageDescription>
            </div>
          </div>
        </PageHeader>
        <PageBody width="narrow">
          <Card>
            <CardHeader>
              <CardTitle>Project details</CardTitle>
              <CardDescription>
                These values are shown on the project card and used in
                report references.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="project-key">Project key</FieldLabel>
                  <Input
                    id="project-key"
                    name="key"
                    required
                    pattern="[a-z0-9][a-z0-9_-]{1,30}"
                    placeholder="starfall"
                    className="font-mono"
                  />
                  <FieldDescription>
                    Lowercase letters, digits, _ and -. Used in report
                    references like{" "}
                    <span className="font-mono">STARFALL-1</span>.
                  </FieldDescription>
                </Field>

                <Field>
                  <FieldLabel htmlFor="project-name">Display name</FieldLabel>
                  <Input
                    id="project-name"
                    name="name"
                    required
                    maxLength={120}
                    placeholder="Starfall"
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="project-desc">Description</FieldLabel>
                  <Textarea
                    id="project-desc"
                    name="description"
                    rows={3}
                    maxLength={500}
                    placeholder="Optional. Internal description shown on the project card."
                  />
                </Field>

                <Field>
                  <FieldLabel>Accent color</FieldLabel>
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className="inline-block size-6 rounded-md border"
                      style={{ background: color }}
                      data-testid="color-chip"
                      aria-hidden="true"
                    />
                    <input name="color" type="hidden" value={color} />
                    <ToggleGroup
                      value={[color]}
                      onValueChange={(value: string[]) => value[0] && setColor(value[0])}
                      aria-label="Accent color"
                      spacing={4}
                      className="flex-wrap"
                    >
                      {SUGGESTED_COLORS.map((c) => (
                        <ToggleGroupItem
                          key={c}
                          value={c}
                          className="size-6 rounded-md border border-border p-0 data-[state=on]:ring-2 data-[state=on]:ring-ring data-[state=on]:ring-offset-2 data-[state=on]:ring-offset-background"
                          data-color={c}
                          aria-label={`Color ${c}`}
                          style={{ background: c }}
                        />
                      ))}
                    </ToggleGroup>
                  </div>
                </Field>
              </FieldGroup>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Cancel
            </Button>
            {create.isPending ? (
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner />
                <span>Creating project…</span>
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={create.isPending}
              className="ml-auto shadow-sm"
            >
              <PlusIcon /> Create project
            </Button>
          </div>
        </PageBody>
      </form>
    </Page>
  );
}
